import type { FC, ReactNode } from "react";
import { Alert } from "@hightouch/htui";

import { QueryType, type ManagedQueryType } from "src/types/models";

type Props = {
  type: ManagedQueryType;
  actions?: ReactNode;
  overridden?: boolean;
};

const getCopy = (
  type: ManagedQueryType,
): { title: string; message: string } => {
  switch (type) {
    case QueryType.JourneyNode:
      return {
        title: "Journey tile managed model",
        message:
          "This query is managed by Hightouch and associated with a journey tile",
      };
    case QueryType.DecisionEngine:
      return {
        title: "AI Decisioning managed model",
        message:
          "This query is managed by Hightouch and powers AI Decisioning message sending",
      };
    case QueryType.IDRGoldenRecordParent:
    case QueryType.IDRGoldenRecord:
      return {
        title: "Golden Record managed model",
        message:
          "This query is managed by Hightouch and associated with an identity graph and its golden record configuration",
      };
    case QueryType.Predictive:
      return {
        title: "Predictive Traits managed model",
        message:
          "This query is managed by Hightouch and associated with a predictive trait",
      };
    case QueryType.CepManagedModel:
      return {
        title: "Native Delivery managed model",
        message:
          "This query is managed by Hightouch and is associated with Native Delivery",
      };
    case QueryType.RealtimeTraitWarehouseState:
      return {
        title: "Realtime traits managed model",
        message:
          "This query is managed by Hightouch and produces warehouse-state values for the parent model's realtime traits",
      };
    case QueryType.LcsTriggeredSend:
      return {
        title: "Lifecycle Studio managed model",
        message:
          "This query is managed by Hightouch and powers a Lifecycle Studio triggered send, computing each recipient's personalization fields",
      };
    case QueryType.CepSuppressionList:
      return {
        title: "Native Delivery suppression list managed model",
        message:
          "This query is managed by Hightouch and lists the email addresses and phone numbers a Native Delivery suppression list matches",
      };
    default:
      throw new Error(`Unknown type: ${type satisfies never}`);
  }
};

export const ManagedQuery: FC<Readonly<Props>> = ({
  type,
  actions,
  overridden,
}) => {
  const { title, message } = getCopy(type);

  return (
    <Alert
      actions={actions}
      title={title}
      message={overridden ? `This managed query has been overridden` : message}
      variant="inline"
      type={overridden ? "warning" : "info"}
    />
  );
};
